export type VenueCategory = "hotel" | "event-centre" | "open-space";

export interface RoomType {
  id: string;
  name: string;
  price: number;
  description: string;
  maxGuests: number;
  quantity: number;
  images: string[];
  amenities?: string[];
}

export const DEFAULT_ROOM_TYPE_PRESETS: (Omit<RoomType, "id"> & { icon: string; shortLabel: string })[] = [
  { name: "Standard Room", icon: "🛏️", shortLabel: "Basic & affordable", price: 0, description: "Comfortable room with bed, AC, TV and bathroom.", maxGuests: 2, quantity: 1, images: [] },
  { name: "Deluxe Room", icon: "✨", shortLabel: "Bigger & nicer", price: 0, description: "Spacious room with premium bed, city view and minibar.", maxGuests: 2, quantity: 1, images: [] },
  { name: "Executive Suite", icon: "💼", shortLabel: "For business guests", price: 0, description: "Suite with workspace, lounge area and free breakfast.", maxGuests: 3, quantity: 1, images: [] },
  { name: "Gold Room", icon: "👑", shortLabel: "VIP luxury", price: 0, description: "Luxury room with gold decor, minibar and priority service.", maxGuests: 3, quantity: 1, images: [] },
  { name: "Presidential Suite", icon: "🏛️", shortLabel: "The best room", price: 0, description: "Private living room, dining area, butler service and panoramic views.", maxGuests: 4, quantity: 1, images: [] },
  { name: "Family Room", icon: "👨‍👩‍👧‍👦", shortLabel: "For families", price: 0, description: "Extra-large room with connecting beds and kid-friendly amenities.", maxGuests: 5, quantity: 1, images: [] },
  { name: "Single Room", icon: "🧍", shortLabel: "One person", price: 0, description: "Compact room ideal for solo travellers.", maxGuests: 1, quantity: 1, images: [] },
  { name: "Event Hall", icon: "🎉", shortLabel: "For parties & events", price: 0, description: "Large hall for weddings, conferences and celebrations.", maxGuests: 200, quantity: 1, images: [] },
  { name: "Conference Room", icon: "📊", shortLabel: "For meetings", price: 0, description: "Meeting room with projector, whiteboard and seating.", maxGuests: 30, quantity: 1, images: [] },
  { name: "Open Space / Garden", icon: "🌳", shortLabel: "Outdoor area", price: 0, description: "Open-air garden or lawn for outdoor events.", maxGuests: 100, quantity: 1, images: [] },
];

export const getStartingPrice = (venue: Venue): number => {
  if (venue.price > 0) return venue.price;
  if (venue.roomTypes && venue.roomTypes.length > 0) {
    return Math.min(...venue.roomTypes.map((r) => r.price));
  }
  return venue.price;
};

export const hasGuestRating = (venue: Venue): boolean => venue.reviews > 0;

export const compareByGuestRating = (a: Venue, b: Venue): number => {
  const aReviewed = hasGuestRating(a);
  const bReviewed = hasGuestRating(b);
  if (aReviewed !== bReviewed) return aReviewed ? -1 : 1;
  if (!aReviewed && !bReviewed) return b.bookingCount - a.bookingCount;
  if (b.rating !== a.rating) return b.rating - a.rating;
  return b.reviews - a.reviews;
};

export type FacilityType = "Hotel" | "Resort" | "Apartment" | "Conference Hall" | "Event Center" | "Open Space";

export interface Venue {
  id: string;
  name: string;
  category: VenueCategory;
  facility: FacilityType;
  location: string;
  price: number; // NGN per night/event
  rating: number;
  reviews: number;
  bookingCount: number;
  image: string;
  gallery: string[];
  photoSections?: Record<string, string[]>;
  description: string;
  amenities: string[];
  roomTypes?: RoomType[];
  capacity?: number;
  bedrooms?: number;
  isFeatured?: boolean;
  latitude?: number;
  longitude?: number;
}

export const LOCATIONS = [
  "Ibadan",
  "Ibadan North",
  "Ibadan South-East",
  "Ibadan South-West",
  "Ibadan North-East",
  "Ibadan North-West",
  "Ogbomoso",
  "Ogbomoso North",
  "Ogbomoso South",
  "Oyo",
  "Oyo West",
  "Oyo East",
  "Iseyin",
  "Saki",
  "Saki West",
  "Saki East",
  "Kisi",
  "Igboho",
  "Eruwa",
  "Lanlate",
  "Igbo-Ora",
  "Fiditi",
  "Lalupon",
  "Ayete",
  "Ago-Are",
  "Tede",
  "Igbeti",
  "Otu",
  "Ikoyi-Ile",
  "Okeho",
  "Ilora",
  "Awe",
  "Jobele",
  "Okaka",
  "Iresa-Adu",
  "Ofiki",
  "Idi-Ayunre",
  "Oluyole",
  "Egbeda",
  "Ona-Ara",
  "Akinyele",
  "Lagelu",
  "Ido",
  "Ibarapa North",
  "Ibarapa Central",
  "Ibarapa East",
  "Atiba",
  "Afijio",
  "Surulere",
  "Orelope",
  "Oriire",
  "Iwajowa",
  "Kajola",
  "Itesiwaju",
  "Atisbo",
] as const;
export const FACILITIES = ["Hotel", "Resort", "Apartment", "Conference Hall", "Event Center", "Open Space"] as const;
export const PURPOSES = ["Vacation", "Business", "Event", "Retreat"] as const;
export const PRICE_RANGES = [
  { label: "₦100k – ₦200k", min: 100_000, max: 200_000 },
  { label: "₦300k – ₦500k", min: 300_000, max: 500_000 },
  { label: "₦600k – ₦900k", min: 600_000, max: 900_000 },
  { label: "₦1m+", min: 1_000_000, max: Infinity },
] as const;

export interface BrowsableCategory {
  id: string;
  label: string;
  icon: string;
  category?: VenueCategory;
  facility?: string;
}

export const BROWSABLE_CATEGORIES: BrowsableCategory[] = [
  { id: "all", label: "All", icon: "LayoutGrid" },
  { id: "hotels", label: "Hotels", icon: "Hotel", category: "hotel", facility: "Hotel" },
  { id: "resorts", label: "Resorts", icon: "Palmtree", category: "hotel", facility: "Resort" },
  { id: "apartments", label: "Apartments", icon: "Building2", category: "hotel", facility: "Apartment" },
  { id: "conference-halls", label: "Conference Halls", icon: "Presentation", category: "event-centre", facility: "Conference Hall" },
  { id: "event-centers", label: "Event Centers", icon: "PartyPopper", category: "event-centre", facility: "Event Center" },
  { id: "open-spaces", label: "Open Spaces", icon: "Trees", category: "open-space", facility: "Open Space" },
];

export const CATEGORY_DETAILS: Record<string, { headline: string; description: string; features: string[] }> = {
  "Hotel": {
    headline: "Premium Hotel Stay",
    description: "Enjoy world-class hospitality with room service, housekeeping, and premium amenities.",
    features: ["Room Service", "Daily Housekeeping", "Front Desk 24/7", "Concierge"],
  },
  "Resort": {
    headline: "Luxury Resort Retreat",
    description: "All-in-one resort with pools, spa, dining, sports, and curated leisure experiences on-site.",
    features: ["Swimming Pool", "Spa & Wellness", "Gym & Sports", "Fine Dining", "Kids Club"],
  },
  "Apartment": {
    headline: "Serviced Apartment",
    description: "A home away from home with full kitchen, living space, and self-catering flexibility.",
    features: ["Full Kitchen", "Washing Machine", "Living Room", "Self Check-in"],
  },
  "Conference Hall": {
    headline: "Conference Hall",
    description: "Professional conference spaces with AV equipment, tiered seating, and breakout rooms.",
    features: ["AV Equipment", "Stage & Podium", "Tiered Seating", "Catering Available", "Breakout Rooms"],
  },
  "Event Center": {
    headline: "Event Center",
    description: "Versatile event spaces for weddings, parties, corporate events and celebrations.",
    features: ["Stage", "Catering", "Valet Parking", "AV Suite", "Bridal Lounge"],
  },
  "Open Space": {
    headline: "Open Space / Garden",
    description: "Open-air venues — lawns, rooftops and gardens for outdoor events and brand activations.",
    features: ["Open Lawn", "Natural Lighting", "Generator Backup", "Parking", "Restrooms"],
  },
};

export const FACILITY_PHOTO_LABELS: Record<string, { key: string; label: string; emoji: string; required: boolean; hint: string }[]> = {
  "Hotel": [
    { key: "exterior", label: "Building / Exterior", emoji: "🏨", required: true, hint: "How does your hotel look from outside?" },
    { key: "reception", label: "Reception / Lobby", emoji: "🛎️", required: false, hint: "The entrance and front desk area" },
    { key: "rooms", label: "Rooms & Beds", emoji: "🛏️", required: false, hint: "Show off your best rooms" },
    { key: "bathroom", label: "Bathroom", emoji: "🚿", required: false, hint: "Clean bathrooms impress guests" },
    { key: "pool", label: "Swimming Pool", emoji: "🏊", required: false, hint: "Pool area (skip if none)" },
    { key: "dining", label: "Restaurant / Bar", emoji: "🍽️", required: false, hint: "Dining and bar area" },
    { key: "gym", label: "Gym / Fitness", emoji: "🏋️", required: false, hint: "Gym or exercise area (optional)" },
  ],
  "Resort": [
    { key: "exterior", label: "Resort Exterior", emoji: "🌴", required: true, hint: "How the resort looks from outside" },
    { key: "pool", label: "Swimming Pool(s)", emoji: "🏊", required: false, hint: "Main pools, infinity pool, lagoon" },
    { key: "spa", label: "Spa & Wellness", emoji: "🧖", required: false, hint: "Spa, sauna, massage rooms" },
    { key: "gym", label: "Gym & Sports", emoji: "🏋️", required: false, hint: "Fitness centre, tennis court, sports" },
    { key: "dining", label: "Restaurants & Bars", emoji: "🍽️", required: false, hint: "Dining venues on the resort" },
    { key: "activities", label: "Activities & Recreation", emoji: "🎾", required: false, hint: "Water sports, golf, kids club" },
    { key: "rooms", label: "Rooms / Villas", emoji: "🛏️", required: false, hint: "Guest rooms and villa interiors" },
    { key: "nightlife", label: "Nightlife / Lounge", emoji: "🎶", required: false, hint: "Bar, lounge, entertainment venue" },
  ],
  "Apartment": [
    { key: "exterior", label: "Building Exterior", emoji: "🏢", required: true, hint: "Outside view of the building (compulsory)" },
    { key: "living", label: "Living Room", emoji: "🛋️", required: false, hint: "The main living/sitting area" },
    { key: "bedroom", label: "Bedroom(s)", emoji: "🛏️", required: false, hint: "Show the sleeping area" },
    { key: "bathroom", label: "Bathroom", emoji: "🚿", required: false, hint: "Bathroom and toilet" },
    { key: "kitchen", label: "Kitchen", emoji: "🍳", required: false, hint: "Kitchen or kitchenette" },
    { key: "view", label: "View / Balcony", emoji: "🪟", required: false, hint: "Window view or balcony" },
  ],
  "Conference Hall": [
    { key: "hall", label: "Main Hall", emoji: "🏛️", required: true, hint: "The main conference/meeting hall (compulsory)" },
    { key: "stage", label: "Stage / Podium", emoji: "🎤", required: false, hint: "Presentation stage or podium" },
    { key: "av", label: "AV Setup", emoji: "📽️", required: false, hint: "Projector, screens, sound system" },
    { key: "seating", label: "Seating Arrangement", emoji: "🪑", required: false, hint: "Audience or delegate seating" },
    { key: "breakout", label: "Breakout Rooms", emoji: "🚪", required: false, hint: "Smaller meeting rooms" },
    { key: "exterior", label: "Building Exterior", emoji: "🏢", required: false, hint: "Outside view of the venue" },
    { key: "parking", label: "Parking Area", emoji: "🅿️", required: false, hint: "Car park and access road" },
  ],
  "Event Center": [
    { key: "hall", label: "Main Event Hall", emoji: "🎉", required: true, hint: "The main event hall (compulsory)" },
    { key: "stage", label: "Stage / Performance Area", emoji: "🎤", required: false, hint: "Stage setup for performers" },
    { key: "exterior", label: "Building Exterior", emoji: "🏢", required: false, hint: "Outside view of the venue" },
    { key: "dining", label: "Dining / Catering Area", emoji: "🍽️", required: false, hint: "Food and catering setup" },
    { key: "bridal", label: "Bridal / VIP Lounge", emoji: "💐", required: false, hint: "Special rooms for VIPs or bridal party" },
    { key: "parking", label: "Parking Area", emoji: "🅿️", required: false, hint: "Car park and access" },
    { key: "av", label: "AV & Lighting", emoji: "💡", required: false, hint: "Sound system and event lighting" },
  ],
  "Open Space": [
    { key: "main", label: "Main Space", emoji: "🌳", required: true, hint: "The main outdoor area (compulsory)" },
    { key: "entrance", label: "Entrance / Gate", emoji: "🚪", required: false, hint: "How guests enter the space" },
    { key: "lighting", label: "Event Lighting", emoji: "💡", required: false, hint: "Festoon lights, string lights, decor" },
    { key: "parking", label: "Parking", emoji: "🅿️", required: false, hint: "Car park area" },
    { key: "restrooms", label: "Restrooms", emoji: "🚻", required: false, hint: "Toilet and bathroom facilities" },
    { key: "stage", label: "Stage / DJ Area", emoji: "🎤", required: false, hint: "Performance or DJ stage" },
  ],
};

export const VENUES: Venue[] = [];

export const getVenue = (id: string) => VENUES.find((v) => v.id === id);
export const formatNaira = (n: number) =>
  new Intl.NumberFormat("en-NG", { style: "currency", currency: "NGN", maximumFractionDigits: 0 }).format(n);


